import type { FC } from 'react';

import {
  Grid,
  Heading,
  InputEmail,
  InputNumber,
  InputTelephone,
  InputText,
  RadioButton,
  RadioGroup,
  TextArea,
} from '@eneco/sparky';
import { Control, Controller, FieldErrors, UseFormRegister } from 'react-hook-form';

import { hintOf } from './hint';
import type { FormValues } from './types';
import type { HeatDamageFormFields } from '../heat-damage-form-content.types';

type Props = {
  register: UseFormRegister<FormValues>;
  formFields: HeatDamageFormFields;
  title: string | undefined;
  control: Control<FormValues, string>;
  errors: FieldErrors<FormValues>;
};

/**
 * Kept as the legacy has it: the surname-preposition input shows the surname field's hint and
 * placeholder, and the salutation looks its labels up under the names `mr`/`mrs`, so content naming
 * its options otherwise (`dhr`/`mevr`, as both CMSes do today) shows the "Dhr."/"Mevr." fallbacks.
 */
export const ContactInputs: FC<Props> = ({ register, formFields, title, errors, control }) => {
  const {
    salutationFormField,
    firstNameFormField,
    surnameFormField,
    surnamePrepositionFormField,
    streetNameFormField,
    houseNumberFormField,
    postalCodeFormField,
    cityFormField,
    phoneNumberFormField,
    emailFormField,
    customerIdFormField,
    remarksFormField,
  } = formFields;
  return (
    <>
      <Heading as="h3" size="S">
        {title}
      </Heading>
      <Controller
        control={control}
        name="salutation"
        render={({ field: { onChange, value, name } }) => (
          <RadioGroup
            name={name}
            value={value}
            onValueChange={onChange}
            label={salutationFormField.label ?? ''}
            error={errors?.salutation?.message}
            hint={hintOf(salutationFormField.hint)}>
            <RadioButton value="dhr">
              {salutationFormField.options?.find(option => option.name === 'mr')?.label ?? `Dhr.`}
            </RadioButton>
            <RadioButton value="mevr">
              {salutationFormField.options?.find(option => option.name === 'mrs')?.label ?? `Mevr.`}
            </RadioButton>
          </RadioGroup>
        )}
      />

      <Grid columns={{ initial: '1', md: '2', lg: '3' }} gap="6">
        <InputText
          label={firstNameFormField.label ?? ''}
          error={errors?.firstname?.message}
          hint={hintOf(firstNameFormField.hint)}
          placeholder={firstNameFormField.placeholder ?? ''}
          {...register('firstname')}
        />
        <InputText
          label={surnamePrepositionFormField.label ?? ''}
          error={errors?.surnamePreposition?.message}
          hint={hintOf(surnameFormField.hint)}
          placeholder={surnameFormField.placeholder ?? ''}
          {...register('surnamePreposition')}
          isOptional
        />
        <InputText
          label={surnameFormField.label ?? ''}
          error={errors?.surname?.message}
          hint={hintOf(surnameFormField.hint)}
          placeholder={surnameFormField.placeholder ?? ''}
          {...register('surname')}
        />
      </Grid>
      <Grid columns={{ initial: '1', md: '3' }} gap="6">
        <Grid.Item gridColumn={{ initial: '1/-1', md: '1/3' }}>
          <InputText
            label={streetNameFormField.label ?? ''}
            error={errors?.street?.message}
            hint={hintOf(streetNameFormField.hint)}
            placeholder={streetNameFormField.placeholder ?? ''}
            {...register('street')}
          />
        </Grid.Item>
        <InputNumber
          label={houseNumberFormField.label ?? ''}
          error={errors?.houseNumber?.message}
          hint={hintOf(houseNumberFormField.hint)}
          placeholder={houseNumberFormField.placeholder ?? ''}
          {...register('houseNumber')}
          formatOptions={{ useGrouping: false }}
        />
      </Grid>
      <Grid columns={{ initial: '1', md: '2' }} gap="6">
        <InputText
          label={postalCodeFormField.label ?? ''}
          error={errors?.postalCode?.message}
          hint={hintOf(postalCodeFormField.hint)}
          placeholder={postalCodeFormField.placeholder ?? ''}
          {...register('postalCode')}
        />
        <InputText
          label={cityFormField.label ?? ''}
          error={errors?.city?.message}
          hint={hintOf(cityFormField.hint)}
          placeholder={cityFormField.placeholder ?? ''}
          {...register('city')}
        />
      </Grid>
      <Grid columns={{ initial: '1', md: '2' }} gap="6">
        <InputTelephone
          label={phoneNumberFormField.label ?? ''}
          autoComplete="tel"
          error={errors?.phoneNumber?.message}
          hint={hintOf(phoneNumberFormField.hint)}
          placeholder={phoneNumberFormField.placeholder ?? ''}
          {...register('phoneNumber')}
        />
        <InputEmail
          label={emailFormField.label ?? ''}
          autoComplete="email"
          error={errors?.email?.message}
          hint={hintOf(emailFormField.hint)}
          placeholder={emailFormField.placeholder ?? ''}
          {...register('email')}
        />
      </Grid>
      <InputNumber
        label={customerIdFormField.label ?? ''}
        error={errors?.customerId?.message}
        hint={hintOf(customerIdFormField.hint)}
        placeholder={customerIdFormField.placeholder ?? ''}
        {...register('customerId')}
        formatOptions={{
          useGrouping: false,
        }}
      />
      <TextArea
        label={remarksFormField.label ?? ''}
        error={errors?.remarks?.message}
        hint={hintOf(remarksFormField.hint)}
        placeholder={remarksFormField.placeholder ?? ''}
        rows={5}
        {...register('remarks')}
        isOptional
      />
    </>
  );
};
